"use client"
import { useEffect, useRef, useState } from "react";
import Script from "next/script"
import { usePathname } from "next/navigation";
import { CookieConsent, type CookieConsentValue, type CookieConsentHandle } from "@/components/arc/cookie-consent/cookie-consent";
import "@/components/ui/animated/foundation.css"
import styles from './consent-manager.module.css'
import { isSensitiveMeasurementPath, sanitizedMeasurementPath, shouldLoadGtm } from "@/modules/measurement/policy";
import type { MeasurementEventName } from "@/modules/measurement/policy";
import { useFullscreenSupportOpen } from "../public-overlay-state";

type Consent = { analytics: boolean; advertising: boolean; clientId: string | null; policyVersion: string }
type Config = { enabled: boolean; firstPartyEnabled?: boolean; webContainerId: string; policyVersion: string;
  events: Record<MeasurementEventName, boolean> }
type State = { config: Config; consent: Consent | null; preferences?: Consent | null }
declare global { interface Window { dataLayer?: unknown[]; danaroMeasurementAllowed?: boolean; danaroGtmLoaded?: boolean;
  danaroFirstPartyAllowed?: boolean;
  danaroMeasurementEvents?: Record<MeasurementEventName, boolean> } }
export function consentEvent(name: Exclude<MeasurementEventName,"purchase">,
  details: Record<string, string | number> = {}) {
  if (typeof window === "undefined" || !window.danaroMeasurementEvents?.[name] || isSensitiveMeasurementPath(window.location.pathname)) return
  const id = crypto.randomUUID()
  if (window.danaroMeasurementAllowed && window.dataLayer) window.dataLayer.push({ event: name === "lead_created" ? "generate_lead" : name === "checkout_started" ? "begin_checkout" : name,
    danaro_event_name: name === "lead_created" ? "generate_lead" : name === "checkout_started" ? "begin_checkout" : name,
    event_id: id, page_location: window.location.origin + sanitizedMeasurementPath(window.location.pathname),
    page_referrer: (() => { try { return document.referrer ? new URL(document.referrer).origin : "" } catch { return "" } })(), ...details })
  if (window.danaroFirstPartyAllowed && name !== "checkout_started" && !(navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl && navigator.doNotTrack !== "1") {
    let source: "direct" | "search" | "social" | "referral" = "direct"
    try {
      const host = document.referrer ? new URL(document.referrer).hostname : ""
      if (host && host !== location.hostname) {
        const is = (domain: string) => host === domain || host.endsWith("." + domain)
        source = ["google.com", "google.co.uk", "bing.com", "duckduckgo.com", "yahoo.com"].some(is) ? "search" : ["facebook.com", "instagram.com", "t.co", "linkedin.com", "tiktok.com"].some(is) ? "social" : "referral"
      }
    } catch { /* Invalid referrers are not retained. */ }
    const path = sanitizedMeasurementPath(window.location.pathname)
    if (/^\/[a-z0-9/_-]{0,239}$/.test(path)) void fetch("/api/measurement/events/", { method: "POST", credentials: "same-origin", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id, name, path, language: /^\/es(?:\/|$)/.test(window.location.pathname) ? "es" : "en", source }) }).catch(() => {})
  }
}
export function ConsentManager({ locale, nonce, privateSurface = false }: { locale: "en" | "es";
  nonce: string; privateSurface?: boolean }) {
  const pathname = usePathname()
  const fullscreenSupportOpen = useFullscreenSupportOpen()
  const [state, setState] = useState<State | null>(null)
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const saving = useRef(false)
  const consentPanel = useRef<CookieConsentHandle>(null)
  const [scriptReady, setScriptReady] = useState(false)
  const lastPageView = useRef("")
  const gtmStarted = useRef(false)
  useEffect(() => {
    const open = () => {
      if (consentPanel.current) consentPanel.current.openPreferences()
      else window.location.assign(locale === 'es' ? '/es/cookies/' : '/cookies/')
    }
    window.addEventListener('danaro-cookie-preferences', open)
    return () => window.removeEventListener('danaro-cookie-preferences', open)
  }, [locale])
  useEffect(() => {
    let active = true
    fetch("/api/measurement/consent/", { cache: "no-store" }).then(async response => {
      if (!response.ok) throw new Error("Preferences unavailable")
      return response.json() as Promise<State>
    }).then(data => { if (active) { window.danaroMeasurementEvents = data.config.events;
      setState(data) } }).catch(() => {})
    return () => { active = false }
  }, [])
  const allowed = !privateSurface && !isSensitiveMeasurementPath(pathname) && state ? shouldLoadGtm(state.config, state.consent) : false
  const firstPartyAllowed = !!(!privateSurface && !isSensitiveMeasurementPath(pathname) && state?.config.firstPartyEnabled && state.consent?.analytics && state.consent.policyVersion === state.config.policyVersion)
  const anyAllowed = allowed || firstPartyAllowed
  useEffect(() => {
    if (isSensitiveMeasurementPath(pathname) && window.danaroGtmLoaded) window.location.replace(window.location.href)
  }, [pathname])
  useEffect(() => {
    if (!allowed) return
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const link = (event.target as Element | null)?.closest?.("a[href]")
      if (!(link instanceof HTMLAnchorElement) || link.target === "_blank" || link.origin !== window.location.origin ||
        !isSensitiveMeasurementPath(link.pathname)) return
      event.preventDefault(); event.stopImmediatePropagation(); window.location.assign(link.href)
    }
    document.addEventListener("click",onClick,true)
    return () => document.removeEventListener("click",onClick,true)
  }, [allowed])
  useEffect(() => { window.danaroMeasurementAllowed = allowed; window.danaroFirstPartyAllowed = firstPartyAllowed
    return () => { window.danaroMeasurementAllowed = false; window.danaroFirstPartyAllowed = false } }, [allowed, firstPartyAllowed])
  useEffect(() => {
    if (!allowed) return
    window.dataLayer = window.dataLayer || []
    function gtag(...args: unknown[]): void
    // Google tags consume the native arguments object for command queue entries.
    // eslint-disable-next-line prefer-rest-params
    function gtag() { window.dataLayer!.push(arguments) }
    gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied",
      ad_user_data: "denied", ad_personalization: "denied" })
    gtag("consent", "update", { analytics_storage: state?.consent?.analytics ? "granted" : "denied",
      ad_storage: state?.consent?.advertising ? "granted" : "denied",
      ad_user_data: state?.consent?.advertising ? "granted" : "denied",
      ad_personalization: state?.consent?.advertising ? "granted" : "denied" })
    if (state?.consent?.analytics && state.consent.clientId) gtag('set', 'client_id', state.consent.clientId)
    if (!gtmStarted.current) {
      window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js",
        page_location: window.location.origin + sanitizedMeasurementPath(pathname),
        page_referrer: (() => { try { return document.referrer ? new URL(document.referrer).origin : "" } catch { return "" } })() })
      gtmStarted.current = true
    }
    let active = true
    queueMicrotask(() => { if (active) setScriptReady(true) })
    return () => { active = false }
  }, [allowed, state?.consent?.analytics, state?.consent?.advertising, state?.consent?.clientId, pathname])
  useEffect(() => { if (allowed && scriptReady) window.danaroGtmLoaded = true }, [allowed,scriptReady])
  useEffect(() => {
    if (anyAllowed && lastPageView.current !== pathname) { lastPageView.current = pathname; consentEvent("page_view", { page_path: sanitizedMeasurementPath(pathname) }) }
  }, [anyAllowed, pathname])
  useEffect(() => {
    if (!anyAllowed) return
    const onClick = (event: MouseEvent) => {
      const link = (event.target as Element | null)?.closest?.("a[href]")
      if (!(link instanceof HTMLAnchorElement)) return
      if (link.protocol === "tel:") consentEvent("telephone_click")
      else if (link.hostname === "book.mylimobiz.com") consentEvent("outbound_booking_click")
    }
    document.addEventListener("click", onClick)
    return () => document.removeEventListener("click", onClick)
  }, [anyAllowed])
  async function decide(value: CookieConsentValue | null) {
    if (saving.current || !state) return
    saving.current = true
    const nextAnalytics = Boolean(value?.analytics), nextAdvertising = Boolean(state.config.enabled && value?.advertising)
    window.danaroMeasurementAllowed = false
    window.danaroFirstPartyAllowed = false
    setBusy(true); setSaveError('')
    try {
      const response = await fetch("/api/measurement/consent/", { method: "POST", credentials: "same-origin", keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analytics: nextAnalytics, advertising: nextAdvertising }) })
      if (!response.ok) throw new Error("Could not save preferences")
      const result = await response.json() as { consent: Consent; preferences: Consent }
      if (window.danaroGtmLoaded && ((!result.consent.analytics && state.consent?.analytics) || (!result.consent.advertising && state.consent?.advertising))) {
        window.location.reload()
        return
      }
      setState(current => current ? { ...current, consent: result.consent, preferences: result.preferences } : current)
      setPreferencesOpen(false)
      const publicSurface = !privateSurface && !isSensitiveMeasurementPath(pathname)
      window.danaroMeasurementAllowed = publicSurface && shouldLoadGtm(state.config, result.consent)
      window.danaroFirstPartyAllowed = Boolean(publicSurface && state.config.firstPartyEnabled && result.consent.analytics)
    } catch {
      setSaveError(locale === 'es' ? 'No pudimos guardar tus preferencias. Inténtalo de nuevo.' : 'We could not save your preferences. Please try again.')
      consentPanel.current?.openPreferences()
    } finally { saving.current = false; setBusy(false) }
  }
  if (!state?.config.enabled && !state?.config.firstPartyEnabled) return null
  const preferences = state.preferences === undefined ? state.consent : state.preferences
  return <>{allowed && scriptReady ? <Script id="danaro-gtm" nonce={nonce} strategy="afterInteractive"
    src={`https://www.googletagmanager.com/gtm.js?id=${state.config.webContainerId}`} /> : null}
    <fieldset className={styles.controls} data-support-open={fullscreenSupportOpen || undefined} inert={fullscreenSupportOpen} aria-hidden={fullscreenSupportOpen || undefined} disabled={busy} aria-busy={busy}>
      <div className="danaro-arc-scope" data-theme="light" data-accent="neutral">
        <CookieConsent ref={consentPanel} className={styles.layer} version={state.config.policyVersion}
          position="bottom" reopenSide="left" preferencesOpen={preferencesOpen}
          onPreferencesOpenChange={open => { if (open || !saving.current) setPreferencesOpen(open) }}
          value={preferences ? { necessary: true, analytics: preferences.analytics, advertising: preferences.advertising } : null}
          categories={[{ id: 'necessary', label: locale === 'es' ? 'Esenciales' : 'Essential', description: locale === 'es' ? 'Seguridad, inicio de sesión y tus solicitudes.' : 'Security, sign-in and your requests.', required: true },
            { id: 'analytics', label: locale === 'es' ? 'Análisis' : 'Analytics', description: locale === 'es' ? 'Nos ayuda a mejorar el sitio y entender las visitas.' : 'Helps us understand visits and improve the website.', defaultEnabled: false },
            ...(state.config.enabled ? [{ id: 'advertising', label: locale === 'es' ? 'Publicidad' : 'Advertising', description: locale === 'es' ? 'Permite medir campañas y personalizar anuncios.' : 'Allows campaign measurement and personalized advertising.', defaultEnabled: false }] : [])]}
          policy={{ label: locale === 'es' ? 'Política de privacidad' : 'Privacy policy', href: locale === 'es' ? '/es/privacy/' : '/privacy/' }}
          labels={{ message: <>{locale === 'es' ? 'Usamos cookies esenciales. Con tu permiso, también usamos cookies para mejorar el sitio y medir campañas.' : 'We use essential cookies. With your permission, we also use cookies to improve the site and measure campaigns.'}{saveError && <span role="alert"> {saveError}</span>}</>,
            customize: locale === 'es' ? 'Preferencias' : 'Preferences', acceptAll: locale === 'es' ? 'Aceptar todo' : 'Accept all', rejectAll: locale === 'es' ? 'Rechazar todo' : 'Reject all',
            save: busy ? locale === 'es' ? 'Guardando…' : 'Saving…' : locale === 'es' ? 'Guardar preferencias' : 'Save choices', preferencesTitle: locale === 'es' ? 'Preferencias de cookies' : 'Cookie preferences',
            preferencesDescription: <>{locale === 'es' ? 'Elige qué cookies opcionales podemos usar. Puedes cambiarlo en cualquier momento.' : 'Choose which optional cookies we can use. You can change this any time.'}{saveError && <span role="alert"> {saveError}</span>}</>,
            reopen: locale === 'es' ? 'Preferencias de cookies' : 'Cookie preferences', alwaysOn: locale === 'es' ? 'Siempre activas' : 'Always on', close: locale === 'es' ? 'Cerrar preferencias' : 'Close preferences' }}
          onChange={value => { void decide(value) }} />
      </div>
    </fieldset>
  </>
}
